// Investment maths. Everything here is pure (no React, no network) so it can be unit-tested.
// Transaction shape (amounts in the holding's own currency, converted with `rate(tx)`):
//   { id, type: "buy"|"sell"|"dividend"|"fee", date: "YYYY-MM-DD", qty, price, fee, amount, fx }
//   buy/sell use qty x price (+ optional fee); dividend/fee use amount. `fx` optionally pins the rate to the
//   profile currency on that day; without it the current rate is used (so FX moves are not counted).

const DAY = 86400000;
const t = (d) => (typeof d === "number" ? d : Date.parse(d + (String(d).length <= 10 ? "T00:00:00Z" : "")));
const ORDER = { buy: 0, dividend: 1, fee: 1, sell: 2 };
const EPS = 1e-9;

// Legacy holdings had one buy price and one quantity. Turn that into a single buy transaction.
export function holdingTx(h) {
  if (Array.isArray(h.tx)) return h.tx;
  if (h.quantity > 0) return [{ id: h.id + "-b", type: "buy", date: h.buyDate || new Date().toISOString().slice(0, 10), qty: Number(h.quantity), price: Number(h.buyPrice) || 0, fee: 0 }];
  return [];
}

export function xirr(flows) {
  const f = flows.filter((x) => x.amt && isFinite(x.amt)).map((x) => ({ t: t(x.t), amt: x.amt }));
  if (f.length < 2 || !f.some((x) => x.amt > 0) || !f.some((x) => x.amt < 0)) return null;
  const t0 = Math.min(...f.map((x) => x.t));
  if (Math.max(...f.map((x) => x.t)) === t0) return null;
  const npv = (r) => f.reduce((s, x) => s + x.amt / Math.pow(1 + r, (x.t - t0) / (365 * DAY)), 0);
  let lo = -0.9999, hi = 1000;
  let flo = npv(lo), fhi = npv(hi);
  if (!isFinite(flo) || !isFinite(fhi) || flo * fhi > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2, fm = npv(mid);
    if (Math.abs(fm) < 1e-7 || hi - lo < 1e-12) return mid;
    if (flo * fm < 0) { hi = mid; fhi = fm; } else { lo = mid; flo = fm; }
  }
  return (lo + hi) / 2;
}

// Replays transactions oldest-first. method "avg" (average cost) or "fifo".
export function replay(txs, { method = "avg", rate = () => 1 } = {}) {
  const sorted = txs.map((x, i) => ({ x, i })).sort((a, b) => (t(a.x.date) - t(b.x.date)) || (ORDER[a.x.type] - ORDER[b.x.type]) || (a.i - b.i)).map((o) => o.x);
  let qty = 0, cost = 0, realised = 0, dividends = 0, fees = 0, bought = 0, sold = 0;
  const lots = [], flows = [], errors = [];
  for (const x of sorted) {
    const r = rate(x);
    const fee = (Number(x.fee) || 0) * r;
    if (x.type === "buy") {
      const q = Number(x.qty) || 0, outlay = q * (Number(x.price) || 0) * r + fee;
      qty += q; cost += outlay; bought += outlay; lots.push({ qty: q, unit: q ? outlay / q : 0 });
      flows.push({ t: x.date, amt: -outlay });
    } else if (x.type === "sell") {
      let q = Number(x.qty) || 0;
      if (q > qty + EPS) { errors.push({ id: x.id, msg: `Sold ${q} but only ${+qty.toFixed(6)} held on ${x.date}` }); q = qty; }
      const proceeds = q * (Number(x.price) || 0) * r - fee;
      let basis;
      if (method === "fifo") {
        basis = 0; let left = q;
        while (left > EPS && lots.length) {
          const l = lots[0], take = Math.min(l.qty, left);
          basis += take * l.unit; l.qty -= take; left -= take;
          if (l.qty <= EPS) lots.shift();
        }
      } else {
        basis = qty > 0 ? (cost / qty) * q : 0;
        let left = q;
        while (left > EPS && lots.length) { const l = lots[0], take = Math.min(l.qty, left); l.qty -= take; left -= take; if (l.qty <= EPS) lots.shift(); }
      }
      qty -= q; cost -= basis; sold += proceeds; realised += proceeds - basis;
      if (qty <= EPS) { qty = 0; cost = 0; }
      flows.push({ t: x.date, amt: proceeds });
    } else if (x.type === "dividend") {
      const a = (Number(x.amount) || 0) * r; dividends += a; flows.push({ t: x.date, amt: a });
    } else if (x.type === "fee") {
      const a = (Number(x.amount) || 0) * r; fees += a; flows.push({ t: x.date, amt: -a });
    }
  }
  return { qty, cost, realised, dividends, fees, bought, sold, flows, errors, first: sorted[0]?.date || null };
}

// Everything the UI needs about one holding. `quote` = { price, currency, prevClose, changePct }.
// `fxNow` = trade currency -> profile currency (for cost); `fxPrice` = quote currency -> profile currency (for value). Usually the same.
export function summarize(h, { quote, fxNow = 1, fxPrice = fxNow, method = "avg", today = new Date().toISOString().slice(0, 10) } = {}) {
  const txs = holdingTx(h);
  const rep = replay(txs, { method, rate: (x) => (x.fx > 0 ? x.fx : fxNow) });
  const price = quote?.price ?? null;
  const hasPrice = price !== null && fxPrice !== null;
  // With no price yet, value the position at cost so totals do not collapse to zero.
  const value = hasPrice ? rep.qty * price * fxPrice : rep.cost;
  const unrealised = value - rep.cost;
  const totalReturn = unrealised + rep.realised + rep.dividends - rep.fees;
  const flows = rep.qty > 0 ? [...rep.flows, { t: today, amt: value, terminal: true }] : rep.flows;
  let dayChange = null, prev = quote?.prevClose;
  if ((prev == null) && quote?.changePct != null && price != null) prev = price / (1 + quote.changePct / 100);
  if (hasPrice && prev != null && rep.qty > 0) dayChange = rep.qty * (price - prev) * fxPrice;
  return {
    ...rep, price, hasPrice, value, unrealised, totalReturn,
    unrealisedPct: rep.cost > 0 ? (unrealised / rep.cost) * 100 : 0,
    totalReturnPct: rep.bought > 0 ? (totalReturn / rep.bought) * 100 : 0,
    avgCost: rep.qty > 0 ? rep.cost / rep.qty : 0,
    dayChange, dayChangePct: quote?.changePct ?? (dayChange != null && value - dayChange > 0 ? (dayChange / (value - dayChange)) * 100 : null),
    xirr: xirr(flows), allFlows: flows,
  };
}

export function combine(list) {
  const sum = (k) => list.reduce((s, x) => s + (x[k] || 0), 0);
  const flows = list.flatMap((x) => x.allFlows || []);
  const withDay = list.filter((x) => x.dayChange != null);
  const dayChange = withDay.length ? withDay.reduce((s, x) => s + x.dayChange, 0) : null;
  const value = sum("value"), cost = sum("cost"), bought = sum("bought");
  const unrealised = value - cost, totalReturn = unrealised + sum("realised") + sum("dividends") - sum("fees");
  return {
    value, cost, bought, unrealised, realised: sum("realised"), dividends: sum("dividends"), fees: sum("fees"), totalReturn,
    unrealisedPct: cost > 0 ? (unrealised / cost) * 100 : 0, totalReturnPct: bought > 0 ? (totalReturn / bought) * 100 : 0,
    dayChange, dayChangePct: dayChange != null && value - dayChange > 0 ? (dayChange / (value - dayChange)) * 100 : null,
    xirr: xirr(flows), flows, errors: list.flatMap((x) => x.errors || []),
  };
}

// "What if every dollar had gone into the index instead": same cash flows, index units bought/sold on the same dates.
// `series` = [{ t(ms), price }] ascending. Returns { xirr, endValue, approx } — index currency effects are ignored.
export function benchmark(flows, series, today = new Date().toISOString().slice(0, 10)) {
  if (!series || series.length < 2 || !flows.length) return null;
  const at = (d) => {
    const ms = t(d); let lo = 0, hi = series.length - 1, ans = -1;
    while (lo <= hi) { const m = (lo + hi) >> 1; if (series[m].t <= ms + DAY) { ans = m; lo = m + 1; } else hi = m - 1; }
    return ans < 0 ? { p: series[0].price, early: true } : { p: series[ans].price, early: false };
  };
  const real = flows.filter((f) => !f.terminal); // drop the "value today" entries; the index gets its own
  let units = 0, approx = false;
  for (const f of real) { const { p, early } = at(f.t); if (early) approx = true; units += -f.amt / p; }
  const end = units * at(today).p;
  const out = [...real, { t: today, amt: end }];
  return { xirr: xirr(out), endValue: end, approx };
}
